/**
 * Pure presentation-layer helpers shared by the lifted feature
 * components. No platform APIs, no I/O — safe under any browser-class
 * environment plus jsdom / Node.
 */

/**
 * Text for `formatRelativeTime`, supplied by the host (desktop slice 5, R1-f).
 *
 * These used to be English literals inside this file, which meant a shared component
 * library — a package that by design holds no strings — was the one place shipping
 * untranslated UI to 28 locales. The visible symptom was mixed-language output where a
 * translated frame wrapped an English fragment: German users read
 * "Zuletzt verwendet just now", Russian "Последнее использование 5m ago".
 *
 * WHY THE COUNT FORMS ARE FUNCTIONS AND NOT `"$COUNT$m ago"` TEMPLATES. Because in one
 * locale the plural is real. Sampling six languages suggests it never is — Russian and
 * Polish repeat one identical string across all four CLDR categories, French across both,
 * and German/Spanish/English differ only by hardcoding "1" where the placeholder goes,
 * because every one of them ABBREVIATES the unit ("min", "мин.", "Min.") and an
 * abbreviation does not inflect. Checking all 28 rather than six finds the exception:
 * **Greek spells the unit out**, so it declines properly —
 *
 *     πριν 1 λεπτό   (one)      vs   πριν 5 λεπτά   (other)
 *     πριν 1 ώρα     (one)      vs   πριν 5 ώρες    (other)
 *
 * — and a single template would render "πριν 5 λεπτό", a plural count against a singular
 * noun. 54 of 56 plural families collapse; these two do not, and one counterexample is
 * enough. A function lets each host pick a plural with its own mechanism (the extension's
 * `tPlural`, web's `selectPlural`), which is what both already do for every other counted
 * string.
 */
export interface RelativeTimeLabels {
  /** Under a minute old. */
  justNow: string;
  /** Whole minutes, 1–59. Must select a plural form. */
  minutesAgo: (minutes: number) => string;
  /** Whole hours, 1–23. Must select a plural form. */
  hoursAgo: (hours: number) => string;
  /**
   * Exactly one day old. Deliberately the SAME catalog string both hosts already ship for
   * the date-separator `DateLabels.yesterday` — it was translated in all 28 locales on
   * both surfaces the whole time this function was returning the English literal beside it.
   */
  yesterday: string;
}

/**
 * Format a timestamp as a human-readable relative string.
 *
 *   - <60s         → `labels.justNow`
 *   - <60m         → `labels.minutesAgo(m)`
 *   - <24h         → `labels.hoursAgo(h)`
 *   - 1 day ago    → `labels.yesterday`
 *   - older        → locale-formatted "Mon DD" via `Date.toLocaleDateString`
 *
 * `timestamp` is in MILLISECONDS. Server fields are frequently unix seconds — see
 * `apps/web/src/components/Connectors.tsx`, which multiplies by 1000 at the call site.
 *
 * Only the final branch was ever localized. Locale defaults to the user's browser locale
 * via `undefined`. Calls `Date.now()` internally — pass a fixed clock by computing the
 * timestamp delta yourself if you need deterministic output.
 */
export function formatRelativeTime(timestamp: number, labels: RelativeTimeLabels): string {
  const now = Date.now();
  const diff = Math.max(0, now - timestamp);
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return labels.justNow;
  if (minutes < 60) return labels.minutesAgo(minutes);
  if (hours < 24) return labels.hoursAgo(hours);
  if (days === 1) return labels.yesterday;

  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Local clock time ("14:32" / "2:32 PM") for a message inside a day-grouped list.
 *
 * Deliberately NOT `formatRelativeTime`, despite the SMS design naming it: that helper
 * never returns a time of day past 24h — it yields `yesterday` and then a bare date — so a
 * message rendered under a "Yesterday" day header would read "Yesterday" a second time, and
 * one under an older date header would repeat that date on every bubble. Once a list carries
 * day headers, the per-item slot wants the time WITHIN the day, which is the one thing
 * `formatRelativeTime` cannot express.
 *
 * Hour/minute only. Locale and 12h/24h convention come from the browser via `undefined`,
 * so this adds no locale keys.
 *
 * Returns "" for a falsy timestamp, matching the local `formatTime` helpers it replaces —
 * an absent time renders as nothing rather than as the epoch.
 */
export function formatClockTime(timestamp: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Convert a base64-encoded image payload to a `data:image/<mime>;base64,...`
 * URL by sniffing the magic-byte prefix of the encoded string.
 *
 * Android mirrors notification images and app icons as raw base64
 * without a mime hint in the wire format — signing the mime in the
 * payload is a pending schema change. Until then, the magic-byte
 * prefixes of the base64 encoding are a reliable sniff:
 *   - PNG  → `iVBOR`         (0x89 P N G)
 *   - WebP → `UklGR`         (R I F F ... W E B P)
 *   - GIF  → `R0lGO`         (G I F 8 _)
 *   - JPEG → `/9j/`          (0xFF 0xD8 0xFF) — fall-through default
 *
 * JPEG is the fall-through. Any unrecognized prefix also falls to JPEG,
 * matching the legacy assumption — harmless for valid content.
 */
export function base64ImageDataUrl(base64: string): string {
  let mime: "jpeg" | "png" | "webp" | "gif" = "jpeg";
  if (base64.startsWith("iVBOR")) mime = "png";
  else if (base64.startsWith("UklGR")) mime = "webp";
  else if (base64.startsWith("R0lGO")) mime = "gif";
  return `data:image/${mime};base64,${base64}`;
}
